// Mixpanel event taxonomy. See docs/epics/mixpanel-analytics.md.

export type EventName =
  | 'progress_tracker_created'
  | 'progress_check_in_completed'
  | 'App Opened'
  | 'notification_permission_preprompt_shown'
  | 'notification_permission_preprompt_accept'
  | 'notification_permission_preprompt_later'
  | 'notification_permission_granted'
  | 'notification_permission_denied'
  | 'review_request_eligible'
  | 'review_request_attempted'
  | 'Page Viewed'
  | 'Sign Up Started'
  | 'Email Submitted'
  | 'Email Verified'
  | 'Onboarding Started'
  | 'Onboarding Completed'
  | 'Room Joined'
  | 'Vent Posted'
  | 'Message Sent'
  | 'Therapist Viewed'
  | 'Tour Started'
  | 'Tour Completed'
  | 'Tour Skipped'
  | 'Weekly Review Available'
  | 'Weekly Review Opened'
  | 'Weekly Review Completed'
  | 'Weekly Review Dismissed'
  | 'Badge Earned'
  | 'Badge Viewed'
  | 'Badges Profile Opened'
  | 'Signup Started'
  | 'Signup Completed'
  | 'Login Completed'
  | 'OAuth Started'
  | 'OAuth Completed'
  | 'OAuth Failed'
  | 'wear_mission_banner_viewed'
  | 'sponsor_banner_viewed'
  | 'sponsor_banner_clicked'
  | 'wear_mission_banner_clicked'
  | 'wear_mission_banner_dismissed'
  | 'wear_mission_page_viewed'
  | 'wear_mission_product_viewed'
  | 'wear_mission_shop_clicked'
  | 'wear_mission_optin_started'
  | 'wear_mission_optin_submitted'
  | 'wear_mission_optin_confirmed'
  | 'wear_mission_unsubscribed'
  | 'onboarding_completed'
  | 'topic_room_selected'
  | 'vent_posted'
  | 'reply_received'
  | 'reply_sent'
  | 'wellness_journal_started'
  | 'journal_entry_submitted'
  | 'checklist_completed'
  | 'coping_strategy_used'
  | 'session_returned'
  | 'vent_reaction_added'
  | 'crisis_resource_tapped'
  | 'question_of_the_day_answered'
  | 'therapist_contact_clicked'
  | 'wellness_streak_milestone';

export type AuthMethod = 'email' | 'google' | 'apple';

export type CharCountBucket = 'xs' | 's' | 'm' | 'l' | 'xl';

export function bucketCharCount(n: number): CharCountBucket {
  if (n < 50) return 'xs';
  if (n < 200) return 's';
  if (n < 500) return 'm';
  if (n < 1000) return 'l';
  return 'xl';
}

export type EventProperties = {
  'App Opened': {
    platform: 'web' | 'ios' | 'android';
    app_version: string;
  };
  'Page Viewed': {
    path: string;
    route_name: string;
  };
  'Sign Up Started': Record<never, never>;
  'Email Submitted': {
    success: boolean;
  };
  'Email Verified': Record<never, never>;
  'Onboarding Started': Record<never, never>;
  'Onboarding Completed': {
    rooms_selected_count: number;
  };
  'Room Joined': {
    room_id: string;
    room_category: string | null;
  };
  'Vent Posted': {
    room_id: string | null;
    char_count_bucket: CharCountBucket;
  };
  'Message Sent': {
    conversation_id: string;
  };
  'Therapist Viewed': {
    therapist_id: string;
  };
  'Tour Started': {
    trigger: 'auto' | 'replay';
  };
  'Tour Completed': Record<never, never>;
  'Tour Skipped': {
    last_step_index: number;
  };
  'Weekly Review Available': {
    week_start: string;
    week_end: string;
    badges_earned: number;
  };
  'Weekly Review Opened': {
    week_start: string;
    source: 'prompt' | 'profile' | 'history';
  };
  'Weekly Review Completed': {
    week_start: string;
    badges_earned: number;
    wellness_tools_completed: number;
  };
  'Weekly Review Dismissed': {
    week_start: string;
    card_index: number;
  };
  'Badge Earned': {
    badge_key: string;
    week_start: string;
    times_earned: number;
  };
  'Badge Viewed': {
    badge_key: string;
    times_earned: number;
  };
  'Badges Profile Opened': {
    badge_count: number;
  };
  notification_permission_preprompt_shown: PromptProps;
  notification_permission_preprompt_accept: PromptProps;
  notification_permission_preprompt_later: PromptProps;
  notification_permission_granted: PromptProps;
  notification_permission_denied: PromptProps;
  review_request_eligible: PromptProps;
  review_request_attempted: PromptProps;
  'Signup Started': { auth_method: AuthMethod };
  'Signup Completed': { auth_method: AuthMethod };
  'Login Completed': { auth_method: AuthMethod };
  'OAuth Started': { auth_method: Exclude<AuthMethod, 'email'> };
  'OAuth Completed': { auth_method: Exclude<AuthMethod, 'email'> };
  'OAuth Failed': {
    auth_method: Exclude<AuthMethod, 'email'>;
    reason: 'canceled' | 'provider_error' | 'exception';
  };
  wear_mission_banner_viewed: Record<never, never>;
  sponsor_banner_viewed: Record<never, never>;
  sponsor_banner_clicked: Record<never, never>;
  wear_mission_banner_clicked: Record<never, never>;
  wear_mission_banner_dismissed: Record<never, never>;
  wear_mission_page_viewed: { source: WearMissionSource };
  wear_mission_product_viewed: { product_handle: string };
  wear_mission_shop_clicked: { product_handle: string | null };
  wear_mission_optin_started: { source: WearMissionSource };
  wear_mission_optin_submitted: { source: WearMissionSource };
  wear_mission_optin_confirmed: Record<never, never>;
  wear_mission_unsubscribed: Record<never, never>;
  onboarding_completed: SafeProps;
  topic_room_selected: SafeProps;
  vent_posted: SafeProps;
  reply_received: SafeProps;
  reply_sent: SafeProps;
  wellness_journal_started: SafeProps;
  journal_entry_submitted: SafeProps;
  checklist_completed: SafeProps;
  coping_strategy_used: SafeProps;
  session_returned: SafeProps;
  vent_reaction_added: SafeProps;
  crisis_resource_tapped: SafeProps;
  question_of_the_day_answered: SafeProps;
  therapist_contact_clicked: SafeProps;
  wellness_streak_milestone: SafeProps;
  progress_tracker_created: SafeProps;
  progress_check_in_completed: SafeProps;
};

export type PropsFor<E extends EventName> = EventProperties[E];

export interface PromptProps {
  trigger_source: string;
  account_age_days?: number;
  active_days?: number;
  meaningful_action_count?: number;
  app_version?: string | null;
  platform?: string;
  attempt?: number;
}

// Interest-only events. Never include emails, user content, or consent evidence.
export type WearMissionSource = 'home' | 'page' | 'settings';

// Counts, categories and dates only — never user text, emails or names.
export type SafeProps = Record<string, string | number | boolean | null>;
